/**
 * Mi Boleta — decisión de consentimiento de cookies.
 *
 * Pieza **pura y sin React**, igual que `flashSaleCta` y `flashSaleCheckout`: si
 * el banner se muestra o no es una decisión verificable **sin montar interfaz**.
 *
 * ⚠️ `localStorage` **puede levantar excepción**, no sólo venir vacío: en modo
 * privado de Safari, con el almacenamiento de sitio bloqueado, o dentro de un
 * iframe con permisos restringidos, **el simple acceso tira**. Por eso cada
 * lectura y cada escritura van envueltas — un `try` que acá no está es una
 * pantalla en blanco para ese visitante.
 *
 * 🔴 **Ante la duda, se pregunta.** Si no se puede leer lo guardado, el banner
 * **se muestra**. Es incómodo —reaparece en cada visita de quien tenga el
 * almacenamiento bloqueado— pero la alternativa es **dar por dado un
 * consentimiento que nadie otorgó**, que es exactamente lo que la figura del
 * consentimiento existe para impedir. La molestia es reversible; afirmar un
 * consentimiento falso, no.
 */

/**
 * Clave **versionada**. Si mañana cambia la política de cookies, subir a `v2`
 * vuelve a preguntarle a todo el mundo — sin la versión habría que adivinar
 * quién aceptó la política vieja y quién la nueva.
 */
export const COOKIE_CONSENT_KEY = 'mb.cookie-consent.v1';

/** Único valor que cuenta como aceptación. Cualquier otra cosa es "no consta". */
const ACCEPTED = 'accepted';

export type CookieConsent = 'accepted' | null;

/** Lo guardado, o `null` si no consta (incluido "no se pudo leer"). */
export function readCookieConsent(): CookieConsent {
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_KEY) === ACCEPTED ? ACCEPTED : null;
  } catch {
    return null;
  }
}

/** ¿Se le muestra el banner a este visitante? */
export function shouldShowCookieBanner(): boolean {
  return readCookieConsent() !== ACCEPTED;
}

/**
 * Registra la aceptación. Devuelve `false` si **no se pudo persistir**.
 *
 * El booleano no es decorativo: quien llama necesita poder distinguir "aceptó y
 * quedó guardado" de "aceptó pero el navegador no deja guardar". En el segundo
 * caso el banner se va de la pantalla igual —el visitante ya respondió, y
 * dejárselo puesto sería no escucharlo— pero volverá en la próxima visita.
 */
export function acceptCookies(): boolean {
  try {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, ACCEPTED);
    return true;
  } catch {
    return false;
  }
}

/**
 * Evento que avisa que el consentimiento cambió.
 *
 * Hace falta porque quien revoca (un enlace en el pie) y quien muestra el
 * banner (el armazón de la página) son **componentes distintos y sin parentesco**.
 * Sin un aviso, el pie borraría el registro y el banner no se enteraría: la
 * decisión quedaría revocada en el almacenamiento pero **invisible en pantalla**
 * hasta recargar — y quien tocó "preferencias" no vería pasar nada.
 */
export const COOKIE_CONSENT_EVENT = 'mb:cookie-consent-cambiado';

/**
 * Borra la decisión guardada para que el banner vuelva a aparecer.
 *
 * Devuelve `false` si no se pudo borrar. El aviso se emite **igual**: aunque el
 * borrado falle, el banner debe reaparecer, porque a partir de acá **el
 * consentimiento no consta** y el estado en pantalla tiene que reflejarlo.
 */
export function revokeCookieConsent(): boolean {
  let borrado = false;
  try {
    window.localStorage.removeItem(COOKIE_CONSENT_KEY);
    borrado = true;
  } catch {
    borrado = false;
  }
  try {
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT));
  } catch {
    // Un entorno sin `CustomEvent` no debe tumbar la revocación: lo importante
    // —que el registro deje de estar— ya pasó.
  }
  return borrado;
}
